import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type {
  onCopyTaskArgs,
  onCopyTaskCallback,
} from 'donor-elf-web/components/task/info';
import { JOURNAL_COPY_AND_PASTE_ALL_CONFIRMATION } from 'donor-elf-web/controllers/funds/fund/journals/journal/index';
import {
  JournalRow,
  JournalStageColumn,
  type onJournalCellChangeCallback,
} from 'donor-elf-web/lib/journal-utils';
import V2TaskModel from 'donor-elf-web/models/v2-task';
import TaskService from 'donor-elf-web/services/task-service';
import moment from 'moment';

interface JournalCellSlimComponentArgs {
  journalRow: JournalRow;
  journalStageColumn: JournalStageColumn;
  allSegments: string[];
  taskToPaste?: V2TaskModel;
  showAssignedUsersIfPresent: boolean;
  onGroupClicked: (groupName: string) => void;
  onSegmentClicked: (segmentName: string) => void;
  onRemoveJournalRow: (journalRow: JournalRow) => void;
  onCopyTask: onCopyTaskCallback;
  onCancelPasteTask: () => void;
  onChange: onJournalCellChangeCallback;
  onJournalRowActivate: (args: {
    journalRow: JournalRow;
    active: boolean;
  }) => void;
}

export default class JournalCellSlimComponent extends Component<JournalCellSlimComponentArgs> {
  @service declare taskService: TaskService;

  tdElement: any;
  mainDropdown?: any;

  // *** start Decision Section ***

  @tracked decisionModalIsOpen = false;

  get isDecisionStageColumn() {
    return this.args.journalStageColumn.isDecisionColumn;
  }

  get hasDecision() {
    return (this.decisionInfos || 0).length > 0;
  }

  get decisionInfos() {
    return this.args.journalRow.journalContactInfo.journalDecisionInfos;
  }

  get decisionIsNo() {
    return this.firstDecisionInfo?.journalDecision.yesNo === false;
  }

  get firstDecisionInfo() {
    return this.decisionInfos[0];
  }

  @action
  setDecisionModalIsOpen(val: boolean) {
    this.decisionModalIsOpen = val;
  }

  onDecisionDivClicked() {
    if (this.decisionModalIsOpen) {
      this.mainDropdown?.actions.close();
    } else {
      this.mainDropdown?.actions.open();
    }
  }

  @action
  handleDecisionChanged() {
    this.mainDropdown?.actions.close();
    this.args.onChange({});
  }

  // *** end Decision Section ***

  // *** start Tasks Section

  @tracked showCompletedTasks = false;

  copyAndPasteAllConfirmation = JOURNAL_COPY_AND_PASTE_ALL_CONFIRMATION;

  get isTaskStageColumn() {
    return (
      this.args.journalStageColumn.isStageColumn &&
      !this.args.journalStageColumn.isDecisionColumn
    );
  }

  get contactTasks() {
    return this.args.journalRow.journalContactInfo.tasks;
  }

  get stageTaskInfos() {
    return this.args.journalRow.taskInfosByStageId[this.stageId];
  }

  get hasTasks() {
    return this.stageTaskInfos.length > 0;
  }

  get allTasksCompleted() {
    return this.stageTaskInfos.every((x) => x.task.isComplete);
  }

  get allCompletedTasksAttempted() {
    return this.completedTaskInfos.every((x) => x.task.isAttemptedResult);
  }

  get completedTaskInfos() {
    return this.stageTaskInfos.filter((x) => x.task.isComplete);
  }

  get notCompletedTaskInfos() {
    return this.stageTaskInfos.filter((x) => !x.task.isComplete);
  }

  get stageId() {
    return this.args.journalStageColumn.stageId;
  }

  get stageTasks() {
    return this.args.journalRow.journalContactInfo.tasksByStageId[this.stageId];
  }

  get newTask() {
    return this.cellState.newTask;
  }

  @action
  handleAddTask(e: any) {
    e.preventDefault();
    e.stopPropagation();
    if (this.cellState.newTask == null) {
      if (this.args.taskToPaste) {
        this.cellState.newTask = this.taskService.copyRecord(
          this.args.taskToPaste,
        );
      } else {
        this.cellState.newTask = this.taskService.createRecord({});
        if (this.args.journalStageColumn.isFollowUpColumn) {
          this.cellState.newTask.dueDate = moment().add(2, 'days').toDate();
        }
      }
      this.cellState.newTask.contactId =
        this.args.journalRow.journalContactInfo.contactId;
      this.cellState.newTask.journalStageId =
        this.args.journalStageColumn.stageId;
      this.args.journalRow.fireCellStateChanged();
    }
  }

  @action
  handleNewTaskClosed(_dropdown: any, e: any) {
    if (e?.type === 'click' && e.target === this.tdElement) {
      return false;
    }
    this.cellState.newTask = undefined;
    this.args.journalRow.fireCellStateChanged();
    return true;
  }

  @action
  handleCopyTask(dropdown: any, copyArgs: onCopyTaskArgs) {
    if (copyArgs.pasteAll) {
      dropdown.actions.close();
    }
    this.args.onCopyTask(copyArgs);
  }

  @action
  handleCancelAddNewTask() {
    this.args.onCancelPasteTask();
  }

  @action
  onNewTaskDivClicked(e: any) {
    e.stopImmediatePropagation();

    if (this.newTask) {
      this.mainDropdown?.actions.close();
      this.deActivateJournalRow();
    } else {
      this.handleAddTask(e);
      this.activateJournalRow();
    }
  }

  @action
  handleTaskSaving() {}

  @action
  async handleTaskSaveError({ task }: { task: V2TaskModel; error: any }) {
    this.args.journalRow.journalContactInfo.recalculate();
    let taskInfo = this.args.journalRow.taskInfosById[task.id];
    if (taskInfo) {
      taskInfo.errorSavingTask = true;
    }
  }

  @action
  handleTaskSaved(task: V2TaskModel) {
    if (this.contactTasks.find((x) => x.id === task.id) == null) {
      this.contactTasks.push(task);
    }
    this.cellState.newTask = null;
    this.args.journalRow.fireCellStateChanged();
    this.args.journalRow.journalContactInfo.recalculate();
    this.args.onChange({ taskSaved: task });
  }

  @action
  handleTaskDeleted(task: V2TaskModel) {
    this.args.journalRow.journalContactInfo.tasks = this.contactTasks.filter(
      (x) => x.id !== task.id,
    );
    this.args.journalRow.journalContactInfo.recalculate();
    this.args.onChange({ taskDeleted: task });
  }

  // *** end Tasks Section ***

  // *** start Finished Section ***

  get isLastStage() {
    return this.args.journalStageColumn.isLastStage;
  }

  @action
  async handleToggleFinished() {
    this.journalContact.finished = !this.journalContact.finished;
    await this.journalContact.save();
    this.args.journalRow.journalContactInfo.recalculate();
    this.args.onChange({});
  }

  // *** end Finished Section ***

  get cellState() {
    return this.args.journalRow.cellState[this.stageId];
  }

  get journalContact() {
    return this.args.journalRow.journalContactInfo.journalContact;
  }

  @action
  templateDidInsert(el: any) {
    if (!this.args.journalStageColumn.isContactColumn) {
      this.tdElement = el.closest('td');
      this.tdElement.addEventListener('click', this.onTdCellClicked);
    }
  }

  @action
  templateWillDestroy() {
    this.tdElement?.removeEventListener('click', this.onTdCellClicked);
  }

  @action
  saveMainDropdown(dropdown: any) {
    this.mainDropdown = dropdown;
  }

  @action
  forgetMainDropdown() {
    this.mainDropdown = undefined;
  }

  @action
  deActivateJournalRow() {
    this.args.onJournalRowActivate({
      journalRow: this.args.journalRow,
      active: false,
    });
  }

  @action
  activateJournalRow() {
    this.args.onJournalRowActivate({
      journalRow: this.args.journalRow,
      active: true,
    });
  }

  @action
  onTdCellClicked(e: any) {
    e.stopImmediatePropagation();

    if (this.isTaskStageColumn) {
      this.onNewTaskDivClicked(e);
    } else if (this.isDecisionStageColumn) {
      this.onDecisionDivClicked();
    }
  }

  // @action
  // async handlePasteTask(e: any) {
  //   e.preventDefault();
  //   e.stopPropagation();
  //   this.cellState.savingTaskToPaste = true;
  //   this.args.journalRow.fireCellStateChanged();
  //   let taskToPaste = this.args.taskToPaste!;
  //   let task = this.taskService.createRecord({
  //     contactId: this.args.journalRow.journalContactInfo.contactId,
  //     journalStageId: this.args.journalStageColumn.stageId,

  //     actionId: taskToPaste.actionId,
  //     mediumId: taskToPaste.mediumId,
  //     resultId: taskToPaste.resultId,
  //     summary: taskToPaste.summary,
  //     notes: taskToPaste.notes,
  //     dueDate: taskToPaste.dueDate,
  //     completedDate: taskToPaste.completedDate,
  //     assignedUserIds: taskToPaste.assignedUserIds,
  //   });
  //   try {
  //     await task.save();
  //   } catch (error) {
  //     this.cellState.savingTaskToPaste = false;
  //     this.cellState.errorSavingPastedTask = true;
  //     this.args.journalRow.fireCellStateChanged();
  //     return;
  //   }

  //   this.contactTasks.push(task);
  //   this.showCompletedTasks = true;
  //   this.args.journalRow.journalContactInfo.recalculate();
  //   this.cellState.showAddButton = false;
  //   this.cellState.savingTaskToPaste = false;
  //   this.args.journalRow.fireCellStateChanged();
  //   this.args.onChange({ taskSaved: task, taskPasted: true });
  // }

  // @action
  // handleDecisionChanged() {
  //   this.cellState.showAddButton = false;
  //   this.args.journalRow.fireCellStateChanged();
  //   this.args.onChange({});
  // }

  // @action
  // async handleToggleFinished() {
  //   this.journalContact.finished = !this.journalContact.finished;
  //   await this.journalContact.save();
  //   this.args.journalRow.journalContactInfo.recalculate();
  //   this.args.onChange({});
  // }
}
